import { ChangeEvent, FC, useEffect, useState } from 'react';

// Import material UI components
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import MainCard from 'ui-component/cards/MainCard';
import { useAgencyStore, usePolicyStore } from 'Application/store';
import { exportToCSV, getAgencyNameFromStorage, getUserRegionFromStorage } from 'Application/utils';
import Loader from 'ui-component/Loader';
import { DownloadRounded, Satellite, Search } from '@mui/icons-material';
import { PolicyStatus, QuoteStatus } from 'Application/models';
import { Box } from '@mui/system';
import CompletedTransactionsList from './quotes';
import { Button, Divider, FormControl, InputAdornment, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // Import DatePicker from MUI or use a similar date library
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useLocation, useNavigate } from 'react-router';
const getEnumFromString = (value: string): PolicyStatus | undefined => {
    return PolicyStatus[value as keyof typeof PolicyStatus];
};

interface CompletedTransactionsProps {
    agencyName: string;
}

export const CompletedTransactions: FC<CompletedTransactionsProps> = ({ agencyName }) => {
    // const { getAllPolicy, isLoading, isError, error } = usePolicyStore(); // Keep isLoading for fetching policies only
    // const [policies, setPolicies] = useState([]);
    const [page, setPage] = useState<number>(1);
    const navigate = useNavigate();

    const [pageSize, setPageSize] = useState<number>(10);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [policyNumber, setPolicyNumber] = useState<string>('');
    const handlePolicyNumberChange = (e: ChangeEvent<HTMLInputElement>) => setPolicyNumber(e.target.value);
    const { pathname } = useLocation();
    const pathnameRole = pathname.split('/')[1];
    const {
        getAgencyPolicies,
        agencyPoliciesData,
        isLoading: isGetPoliciesLoading,
        isError: isGetPoliciesError,
        error: getPoliciesError
    } = useAgencyStore((state) => ({
        getAgencyPolicies: state.getAgencyPolicies,
        agencyPoliciesData: state.agencyPoliciesData,
        isLoading: state.isLoading,
        isError: state.isError,
        error: state.error
    }));

    const policyStatusOptions = ['ALL', ...Object.values(PolicyStatus).map((statusOption) => statusOption)];
    const [policyStatus, setPolicyStatus] = useState<string>(policyStatusOptions[0]);

    const handlePolicyStatusChange = (newPolicyStatus: string) => {
        setPolicyNumber('');
        setPolicyStatus(newPolicyStatus);
    };

    const policyStatusOption = getEnumFromString(policyStatus);

    useEffect(() => {
        console.log('Executing  getting changes in useEffect'.toUpperCase());
        getAgencyPolicies(agencyName, page, pageSize, policyStatusOption, policyNumber, startDate, endDate);
    }, [agencyName, page, pageSize, policyStatusOption, policyNumber, startDate, endDate]);

    const handlePolicyNumberSearch = async () => {
        console.log('Executing the policy number search'.toUpperCase());
        await getAgencyPolicies(agencyName, page, pageSize, policyStatusOption, policyNumber);
    };

    const handleDateFilter = async () => {
        console.log('Executing the date filter'.toUpperCase());
        await getAgencyPolicies(agencyName, page, pageSize, policyStatusOption, policyNumber, startDate, endDate);
    };

    const handleDataExport = () => {
        const exportContent = agencyPoliciesData?.content!.map((policy) => ({
            'Policy number': policy.policyNumber,
            'Issue date': policy.createdAt,
            'Amount Paid (KSH)': policy.payment.amount,
            'Main customer': `${policy.quotation.customer.firstName} ${policy.quotation.customer.otherNames}`,
            'Email address': policy.quotation.customer.emailAddress,
            'Travel plan': policy.quotation.travelPlan.name,
            'Start date': policy.quotation.departureDate,
            'Expiry date': policy.quotation.returnDate
        }));

        exportToCSV('Policies', exportContent ?? []);
    };

    // console.log(`Is the get policies loading: ${isGetPoliciesLoading}`.toUpperCase());
    // console.log(`Is agenciesPolicyData null: ${agencyPoliciesData}`.toUpperCase());
    // console.log("regionName", regionName);

    const regionName = getUserRegionFromStorage();
    const currency = regionName === 'Kenya' ? 'KSH' : regionName === 'Rwanda' ? 'RWF' : '';
    const identificationType = regionName === 'Kenya' ? 'Passport Number' : regionName === 'Rwanda' ? 'Identification Number' : '';
    const handleRedirect = (policyNumber: string) => {
        if (regionName === 'Rwanda') {
            navigate(`/${pathnameRole}/travel-portal/completed-transactions/viewpolicy-kyc-rw/${policyNumber}`);
        } else {
            navigate(`/${pathnameRole}/travel-portal/completed-transactions/viewpolicy/${policyNumber}`);
        }
    };
    return (
        <>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container className="flex items-center" spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Typography>Filter by status, date, or search for a specific policy</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} className="flex space-y-2 sm:flex-row flex-col sm:space-x-2 sm:space-y-0">
                        <Box className="sm:w-1/4 w-full">
                            <FormControl fullWidth>
                                <InputLabel id="policy-status-label">Policy Status</InputLabel>
                                <Select
                                    // size="small"
                                    labelId="policy-status-label"
                                    id="policy-status"
                                    value={policyStatus}
                                    fullWidth
                                    label="Policy Status"
                                    variant="outlined"
                                    onChange={(e) => handlePolicyStatusChange(e.target.value)}
                                >
                                    {policyStatusOptions.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status.toUpperCase()}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box className="sm:w-1/4 w-full">
                            <TextField
                                // size="small"
                                id="policyNumber"
                                label="Policy Number"
                                variant="outlined"
                                value={policyNumber}
                                onChange={handlePolicyNumberChange}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Search className="hover:cursor-pointer" onClick={handlePolicyNumberSearch} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>
                        <Box className="sm:w-1/2 w-full flex space-x-2">
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newValue) => setStartDate(newValue)}
                                renderInput={(params) => <TextField size="small" {...params} />}
                            />
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                renderInput={(params) => <TextField size="small" {...params} />}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} className="text-right space-x-2">
                        <Button variant="contained" color="primary" onClick={handleDateFilter}>
                            Apply Filters
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                                setPolicyNumber('');
                                setStartDate(null);
                                setEndDate(null);
                                setPolicyStatus(policyStatusOptions[0]); // Reset to default status
                            }}
                        >
                            Clear Filters
                        </Button>

                        {!isGetPoliciesLoading && (
                            <Button onClick={handleDataExport} endIcon={<DownloadRounded />} disableElevation variant="contained">
                                Export data
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </LocalizationProvider>

            <Divider className="my-6" />

            {/* Show loader only when fetching policies */}
            <Box>
                {isGetPoliciesLoading || agencyPoliciesData === null ? (
                    <Loader />
                ) : isGetPoliciesError ? (
                    <Typography color="error">{getPoliciesError}</Typography>
                ) : (
                    <>
                        <CompletedTransactionsList
                            policies={agencyPoliciesData}
                            currency={currency}
                            identificationType={identificationType}
                            handleRedirect={handleRedirect}
                        />
                        <Grid container justifyContent="space-between">
                            {agencyPoliciesData.totalPages > 1 && (
                                <Pagination
                                    className="mt-6"
                                    count={agencyPoliciesData?.totalPages}
                                    page={page}
                                    onChange={(event, value) => setPage(value)}
                                    color="primary"
                                />
                            )}
                        </Grid>
                    </>
                )}
            </Box>
        </>
    );
};

export const AgencyUserCompletedTransactions = () => {
    const agencyName: string = getAgencyNameFromStorage()!;

    return (
        <MainCard title={<Typography variant="h3">Completed Transactions</Typography>}>
            <CompletedTransactions agencyName={agencyName} />
        </MainCard>
    );
};

// agencyName, page, pageSize, policyStatusOption, policyNumber, startDate, endDate
